import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { ActionButton } from '@/shared/ui/action-button';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';

import { getPriorityOptions } from '../model';

interface ChangePriorityDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onConfirm: (priority: string, comments: string) => void;
}

export function ChangePriorityDialog({ open, onOpenChange, onConfirm }: ChangePriorityDialogProps) {
  const { t } = useTranslation();

  const [priority, setPriority] = useState('urgent');
  const [comments, setComments] = useState('');

  const PRIORITY_OPTIONS = getPriorityOptions(t);

  const handleSubmit = () => {
    onConfirm(priority, comments);
    setPriority('urgent');
    setComments('');
    onOpenChange(false);
  };

  const handleClose = () => {
    setPriority('urgent');
    setComments('');
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle>{t('cmrlist.changepriorities.title')}</DialogTitle>
        </DialogHeader>

        <div className="space-y-4 py-4">
          <div className="space-y-2">
            <Label htmlFor="priority">{t('cmrlist.newdispatchPriority.label')}</Label>
            <Select value={priority} onValueChange={setPriority}>
              <SelectTrigger id="priority">
                <SelectValue placeholder={t('cmrlist.newdispatchPriority.placeholder')} />
              </SelectTrigger>
              <SelectContent>
                {PRIORITY_OPTIONS.map((option) => (
                  <SelectItem key={option.value} value={option.value}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label htmlFor="comments">{t('newcmr.reqstatus.comment.label')}</Label>
            <Textarea
              id="comments"
              value={comments}
              onChange={(e) => setComments(e.target.value)}
              placeholder={t('requestHistory.remarks.desc')}
              className="min-h-32"
            />
          </div>
        </div>

        <DialogFooter className="gap-2">
          <ActionButton intent="dismiss" onClick={handleClose}>
            {t('gen.closedialog.label')}
          </ActionButton>
          <ActionButton intent="primary" onClick={handleSubmit} className="min-w-24">
            {t('gen.save.label')}
          </ActionButton>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
